import React, { useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import type { TrendyolOrder, TrendyolOrderLine, TrendyolOrderModifier } from '../types';
import { printPackageReceipt } from '../components/PackageReceiptPrintService';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef } from '../utils/receiptHeader';
import { calculateTrendyolSellerDiscount } from '../utils/helpers';
import { buildGoogleMapsUrl } from '../utils/yemeksepetiReceiptBuilder';
import type { PlatformIntegration } from '../../../../types/platformIntegration';

export interface TrendyolOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  order: TrendyolOrder | null;
  trendyolProductDescriptions?: Map<number, string>;
  /**
   * Online ödenmiş siparişler için Pavo cihazından mali fiş kestirme handler'i.
   * Tanimliysa modaldaki "Online Fis Yazdir" butonu aktif olur (sadece online
   * odenmis siparislerde gorunur).
   */
  onRequestOnlineReceipt?: () => void;
  screenData?: {
    id: number;
    localServiceUrl?: string;
    printingSettings?: {
      printingMode: string;
      defaultReceiptPrinter?: { id: string; name: string } | null;
      adisyonPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
    };
    /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
    platformIntegrations?: PlatformIntegration[];
  };
}

const getStatusLabel = (status: string): { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' } => {
  switch (status?.toLowerCase()) {
    case 'created': return { label: 'Yeni Sipariş', color: 'warning' };
    case 'picking': return { label: 'Kabul Edildi', color: 'info' };
    case 'invoiced': return { label: 'Hazırlandı', color: 'primary' };
    case 'shipped': return { label: 'Yolda', color: 'secondary' };
    case 'delivered': return { label: 'Teslim Edildi', color: 'success' };
    case 'unsupplied': return { label: 'İptal (Restoran)', color: 'error' };
    case 'cancelled': return { label: 'İptal (Trendyol)', color: 'error' };
    case 'returned': return { label: 'İade', color: 'error' };
    default: return { label: status || 'Bilinmiyor', color: 'default' };
  }
};

const getPaymentLabel = (payment: TrendyolOrder['payment'] | undefined): string => {
  if (!payment) return 'Bilinmiyor';
  if (payment.paymentType === 'PAY_WITH_ON_DELIVERY') {
    const onDeliveryType = payment.onDelivery?.paymentType || '';
    if (onDeliveryType === 'CASH') return 'Kapıda Nakit';
    return 'Kapıda Kart';
  }
  return 'Online Ödeme';
};

function flattenModifiers(modifiers: TrendyolOrderModifier[], depth = 0): { name: string; price: number; depth: number }[] {
  const result: { name: string; price: number; depth: number }[] = [];
  for (const mod of modifiers) {
    result.push({ name: mod.name, price: mod.price, depth });
    if (mod.modifierProducts?.length) {
      result.push(...flattenModifiers(mod.modifierProducts, depth + 1));
    }
  }
  return result;
}

const TrendyolOrderDetailModal: React.FC<TrendyolOrderDetailModalProps> = ({
  open,
  onClose,
  order,
  trendyolProductDescriptions,
  screenData,
  onRequestOnlineReceipt,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const statusInfo = useMemo(() => getStatusLabel(order?.packageStatus || ''), [order?.packageStatus]);
  const paymentLabel = useMemo(() => getPaymentLabel(order?.payment), [order?.payment]);

  const creationDate = useMemo(() => {
    if (!order?.packageCreationDate) return '';
    return new Date(order.packageCreationDate).toLocaleString('tr-TR');
  }, [order?.packageCreationDate]);

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;

    const printer = screenData?.printingSettings?.defaultReceiptPrinter
      || screenData?.printingSettings?.adisyonPrinter;
    if (!printer) {
      alert('Fiş yazıcısı tanımlanmamış. Lütfen ekran ayarlarından yazıcı seçin.');
      return;
    }

    setIsPrinting(true);
    try {
      const localServiceUrl = screenData?.localServiceUrl || 'http://localhost:3003';
      // Markaya özel fiş başlığı; marka tanımlı değilse ekran varsayılanı.
      const brandHeader = resolveReceiptHeader(buildReceiptOrderRef('trendyol_yemek', null, order), screenData);
      const orderDate = order.packageCreationDate ? new Date(order.packageCreationDate) : new Date();
      const deliveryLabel = order.deliveryType === 'GO' ? 'Uber Eats Kurye (GO)' : 'Restoran Kuryesi (STORE)';

      const isGoDelivery = order.deliveryType === 'GO';
      let deliveryAddress = '';
      let addrDescription: string | undefined;
      if (order.address && !isGoDelivery) {
        const mainAddr = [order.address.address1, order.address.neighborhood, order.address.district, order.address.city].filter(Boolean).join(', ');
        const detailAddr = [
          order.address.apartmentNumber ? `Apt: ${order.address.apartmentNumber}` : '',
          order.address.floor ? `Kat: ${order.address.floor}` : '',
          order.address.doorNumber ? `Kapı: ${order.address.doorNumber}` : '',
        ].filter(Boolean).join(' ');
        deliveryAddress = [mainAddr, detailAddr].filter(Boolean).join(' - ');
        addrDescription = order.address.addressDescription || undefined;
      }
      // Uber fisindeki "musteri adresine erisim" QR'i. GO teslimatta adres
      // paylasilmadigi icin koordinat da gelmez; o durumda QR basilmaz.
      const mapsUrl = isGoDelivery
        ? undefined
        : buildGoogleMapsUrl(Number(order.address?.latitude), Number(order.address?.longitude));

      const receiptItems = (order.lines || []).map(line => {
        const subItems: { name: string }[] = [];
        if (line.modifierProducts?.length) {
          const flat = flattenModifiers(line.modifierProducts);
          for (const mod of flat) {
            subItems.push({ name: `${'  '.repeat(mod.depth)}+ ${mod.name}${mod.price > 0 ? ` (+${mod.price.toFixed(2)})` : ''}` });
          }
        }
        if (line.extraIngredients?.length) {
          for (const extra of line.extraIngredients) {
            subItems.push({ name: `+ ${extra.name}${extra.price > 0 ? ` (+${extra.price.toFixed(2)})` : ''}` });
          }
        }
        if (line.removedIngredients?.length) {
          for (const removed of line.removedIngredients) {
            subItems.push({ name: `- ${removed.name} (çıkarıldı)` });
          }
        }

        // Adet: iptal olmayan ürün sayısı kadar
        const quantity = line.items?.filter(i => !i.isCancelled).length || 1;

        const desc = trendyolProductDescriptions?.get(line.productId);
        return {
          name: line.name,
          description: desc || undefined,
          quantity,
          unitPrice: line.unitSellingPrice || line.price || 0,
          totalPrice: (line.unitSellingPrice || line.price || 0) * quantity,
          subItems: subItems.length > 0 ? subItems : undefined,
        };
      });

      const itemsTotal = receiptItems.reduce((sum, it) => sum + it.totalPrice, 0);
      const rawTotal = order.totalPrice || 0;
      const discountTotal = calculateTrendyolSellerDiscount(order);
      const grandTotal = rawTotal - discountTotal;

      const receiptData = {
        items: receiptItems,
        orderNumber: order.orderNumber || order.orderCode || '',
        shortCode: order.orderCode || undefined,
        customerName: order.customer ? `${order.customer.firstName} ${order.customer.lastName}` : 'Müşteri',
        customerPhone: order.address?.phone || order.callCenterPhone || '',
        customerAddress: deliveryAddress || undefined,
        addressDescription: addrDescription,
        date: orderDate.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
        time: orderDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
        grandTotal,
        subtotal: discountTotal > 0 ? rawTotal : (itemsTotal > grandTotal ? itemsTotal : grandTotal),
        discountTotal,
        vatTotal: 0,
        paymentStatus: order.payment?.paymentType !== 'PAY_WITH_ON_DELIVERY' ? 'PAID' : undefined,
        paymentMethod: order.payment?.paymentType !== 'PAY_WITH_ON_DELIVERY' ? 'Online Ödeme' : 'Kapıda Ödeme',
        isOnlinePaid: order.payment?.paymentType !== 'PAY_WITH_ON_DELIVERY',
        customerNote: order.customerNote || undefined,
        ...brandHeader,
        logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
        trendyolOrderCode: order.orderNumber || order.orderCode || undefined,
        callCenterPhone: order.callCenterPhone || undefined,
        // Fisin orta bolumu Uber'in kendi duzeninde bassin.
        isUberEatsReceipt: true,
        supportCode: order.supplierId ? String(order.supplierId) : undefined,
        qrCodeRawUrl: mapsUrl,
        qrCodeLabel: mapsUrl ? 'ADRES QR' : undefined,
        receiptLabel: resolveReceiptLabel(
          'UBER EATS PAKET SIPARIS',
          buildReceiptOrderRef('trendyol_yemek', null, order),
          screenData,
        ),
        deliveryType: deliveryLabel,
      };

      await printPackageReceipt(receiptData, printer, localServiceUrl);
      console.log('[TrendyolDetail] Fiş yazdırıldı');
    } catch (err: any) {
      console.error('[TrendyolDetail] Fiş yazdırma hatası:', err);
      alert(`Fiş yazdırma hatası: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenData]);

  if (!order) return null;

  const renderLineItem = (line: TrendyolOrderLine, index: number) => {
    const modifiers = flattenModifiers(line.modifierProducts || []);
    const quantity = line.items?.filter(i => !i.isCancelled).length || 1;
    const lineTotal = (line.unitSellingPrice || line.price || 0) * quantity;
    const lineDescription = trendyolProductDescriptions?.get(line.productId);
    return (
      <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {quantity > 1 ? `${quantity}x ` : ''}{line.name}
            </Typography>
            {lineDescription && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                {lineDescription}
              </Typography>
            )}
            {quantity > 1 && (
              <Typography variant="caption" color="text.secondary">
                Birim: {(line.unitSellingPrice || line.price || 0).toFixed(2)} TL
              </Typography>
            )}
          </Box>
          <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 70, textAlign: 'right' }}>
            {lineTotal.toFixed(2)} TL
          </Typography>
        </Box>

        {modifiers.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {modifiers.map((mod, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', pl: mod.depth * 2 }}>
                + {mod.name}{mod.price > 0 ? ` (+${mod.price.toFixed(2)} TL)` : ''}
              </Typography>
            ))}
          </Box>
        )}

        {line.extraIngredients?.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {line.extraIngredients.map((extra, i) => (
              <Typography key={i} variant="caption" color="success.main" sx={{ display: 'block' }}>
                + {extra.name}{extra.price > 0 ? ` (+${extra.price.toFixed(2)} TL)` : ''}
              </Typography>
            ))}
          </Box>
        )}

        {line.removedIngredients?.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {line.removedIngredients.map((removed, i) => (
              <Typography key={i} variant="caption" color="error.main" sx={{ display: 'block', textDecoration: 'line-through' }}>
                - {removed.name}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TwoWheelerIcon sx={{ color: '#06C167' }} />
          <Typography variant="h6">Uber Eats Sipariş Detayı</Typography>
          <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Sipariş Bilgileri */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Sipariş Bilgileri</Typography>
              <Typography variant="body2">Sipariş No: <strong>{order.orderCode || order.orderNumber}</strong></Typography>
              <Typography variant="body2">Sipariş ID: {order.orderId}</Typography>
              <Typography variant="body2">Tarih: {creationDate}</Typography>
              <Typography variant="body2">ETA: {order.eta || '-'}</Typography>
              <Typography variant="body2">Hazırlık Süresi: {order.preparationTime} dk</Typography>
              {order.testPackage && <Chip label="TEST" color="warning" size="small" sx={{ mt: 0.5 }} />}
            </Paper>
          </Grid>

          {/* Teslimat Bilgileri */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {order.deliveryType === 'GO'
                  ? <><LocalShippingIcon fontSize="small" sx={{ color: '#06C167' }} /> Uber Eats Kurye</>
                  : <><StorefrontIcon fontSize="small" /> Restoran Kuryesi</>
                }
              </Typography>
              <Chip
                label={order.deliveryType === 'GO' ? 'Model 2 (GO)' : 'Model 1 (STORE)'}
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              {order.storePickupSelected && (
                <Chip label="Gel Al" color="info" size="small" sx={{ ml: 1, mb: 1 }} />
              )}
              {order.isCourierNearby && (
                <Typography variant="body2" color="success.main">Kurye yakında</Typography>
              )}
            </Paper>
          </Grid>

          {/* Müşteri Bilgileri */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Müşteri</Typography>
              <Typography variant="body2">
                {order.customer?.firstName} {order.customer?.lastName}
              </Typography>
              {order.address?.phone && (
                <Typography variant="body2">Tel: {order.address.phone}</Typography>
              )}
              {order.callCenterPhone && (
                <Typography variant="body2" color="text.secondary">Çağrı Merkezi: {order.callCenterPhone}</Typography>
              )}
            </Paper>
          </Grid>

          {/* Adres */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Teslimat Adresi</Typography>
              {order.deliveryType === 'GO' ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Uber Eats GO kuryesi teslimat yapacak — adres bilgisi Uber Eats tarafından paylaşılmaz.
                </Typography>
              ) : order.address ? (
                <>
                  <Typography variant="body2">{order.address.address1}</Typography>
                  {order.address.neighborhood && <Typography variant="body2">{order.address.neighborhood}, {order.address.district}</Typography>}
                  <Typography variant="body2">{order.address.city}</Typography>
                  {order.address.addressDescription && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Tarif: {order.address.addressDescription}
                    </Typography>
                  )}
                  {(order.address.floor || order.address.doorNumber || order.address.apartmentNumber) && (
                    <Typography variant="body2" color="text.secondary">
                      {[
                        order.address.apartmentNumber && `Apt: ${order.address.apartmentNumber}`,
                        order.address.floor && `Kat: ${order.address.floor}`,
                        order.address.doorNumber && `Kapı: ${order.address.doorNumber}`,
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Adres bilgisi yok</Typography>
              )}
            </Paper>
          </Grid>

          {/* Ödeme */}
          <Grid item xs={12}>
            {(() => {
              const grossTotal = order.totalPrice || 0;
              const sellerDiscount = calculateTrendyolSellerDiscount(order);
              const netTotal = grossTotal - sellerDiscount;
              // Kampanya promotions[]'ta, kupon coupon alaninda gelir; ikisi de
              // ayni indirim kalemi listesinde gosterilir.
              const discountRows = [
                ...(order.promotions || [])
                  .filter(p => (p?.totalSellerAmount || 0) > 0)
                  .map(p => ({ label: p.description || 'Kampanya İndirimi', amount: p.totalSellerAmount || 0 })),
              ];
              const couponAmount = sellerDiscount - discountRows.reduce((sum, r) => sum + r.amount, 0);
              if (couponAmount > 0) {
                discountRows.push({
                  label: order.coupon?.description || 'Kupon İndirimi',
                  amount: couponAmount,
                });
              }

              return (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">Ödeme</Typography>
                      <Typography variant="body2">{paymentLabel}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {sellerDiscount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textDecoration: 'line-through' }}>
                          {grossTotal.toFixed(2)} TL
                        </Typography>
                      )}
                      <Typography variant="h6" fontWeight="bold" color={sellerDiscount > 0 ? 'success.main' : 'primary'}>
                        {netTotal.toFixed(2)} TL
                      </Typography>
                    </Box>
                  </Box>
                  {sellerDiscount > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Ara Toplam</Typography>
                        <Typography variant="caption" color="text.secondary">{grossTotal.toFixed(2)} TL</Typography>
                      </Box>
                      {discountRows.map((row, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="warning.main">{row.label}</Typography>
                          <Typography variant="caption" fontWeight="bold" color="warning.main">
                            -{row.amount.toFixed(2)} TL (satıcı payı)
                          </Typography>
                        </Box>
                      ))}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" fontWeight="bold">Tahsil Edilen Tutar</Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {netTotal.toFixed(2)} TL
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              );
            })()}
          </Grid>

          {/* Ürünler */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Ürünler ({order.lines?.length || 0})
            </Typography>
            {order.lines?.map((line, index) => renderLineItem(line, index))}
          </Grid>

          {/* Müşteri Notu */}
          {order.customerNote && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50', borderColor: 'warning.200' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Müşteri Notu</Typography>
                <Typography variant="body2">{order.customerNote}</Typography>
              </Paper>
            </Grid>
          )}

          {/* İptal Bilgisi */}
          {order.cancelInfo && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50', borderColor: 'error.200' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>İptal Bilgisi</Typography>
                <Typography variant="body2">Neden: {order.cancelInfo.cancelReasonText || `Kod: ${order.cancelInfo.cancelReasonId}`}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={handlePrintReceipt}
            disabled={!order || isPrinting}
            sx={{ bgcolor: '#06C167', '&:hover': { bgcolor: '#05A357' } }}
          >
            {isPrinting ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
          </Button>
          {/* Online ödenmiş Trendyol siparisleri icin REST-P2P uzerinden Pavo
              cihazindan mali fis kestirme. Sadece online odenmis (paymentType
              !== PAY_WITH_ON_DELIVERY) siparislerde gorunur. */}
          {onRequestOnlineReceipt && order && order.payment?.paymentType !== 'PAY_WITH_ON_DELIVERY' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={onRequestOnlineReceipt}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Online Fiş Yazdır
            </Button>
          )}
        </Box>
        <Button variant="outlined" onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendyolOrderDetailModal;
